/**
 * @file `LearnerEvents` — typed in-window event bus the lab SPA
 *       emits onto whenever a UI-observable transition completes.
 *       Event names mirror their backend counterparts so a Playwright
 *       PersonaSession spec can assert on the same identifier the
 *       operator sees in Grafana / Loki.
 * @module @labcraft/lab/ui/learner-events
 *
 * Design rationale (umbrella #701 thread):
 *
 *   - Emission point is the **commit boundary** (a React `useEffect`
 *     post-mount, or the moment a state field becomes "user-visible"),
 *     not the reducer that flipped a state field. The event guarantees
 *     "the component painted" rather than "we read a value."
 *   - Names match backend-emitted event names from
 *     `@labcraft/hypervisor`'s `MachineProvider` events — `suspend`,
 *     `resume`, `lifecycle`. A single LogQL/grep token finds both
 *     causal sides.
 *   - Bus is mounted on `window.__learnerEvents__` so a test can tap
 *     in via `page.evaluate(...)` without an in-browser test-only
 *     side channel. Production code never reads from there; only the
 *     test runner does.
 *
 * The bus extends `CoreClass<TProps, LearnerEventsMap>` so it inherits
 * the typed `.on/.once/.off/.emit` surface from the framework — no
 * sidecar EventEmitter, no per-event wrapper boilerplate.
 */

import { CoreClass } from '@labcraft/core'
import type {
  ChapterRef,
  MachineLifecycleState,
  Role,
} from '../shared/ws_messages.ts'

/**
 * Window key the bus mounts itself onto. Capitalized as `__learnerEvents__`
 * (double-underscore brackets) so a future operator searching the
 * codebase recognizes it as test-bridge plumbing rather than product
 * state.
 */
export const LEARNER_EVENTS_WINDOW_KEY = '__learnerEvents__' as const

/**
 * Typed event map. Names are the **backend** event names so a Playwright
 * spec's `awaitEvent('suspend', ...)` and a Grafana query's
 * `event="suspend"` resolve to the same logical transition.
 *
 * Payload shapes are deliberately small — just enough to drive an
 * assertion. Test specs that need richer context read it from the DOM
 * after the event lands.
 *
 * @property lifecycle - VM lifecycle state transition. Fired by
 *   `TerminalPane` from its `onLifecycle` effect after the overlay
 *   has rendered for the new state.
 * @property suspend - Voluntary or idle suspend committed in the UI
 *   (state ∈ `'pausing' | 'paused' | 'snapshotting' | 'saved'`). Fired
 *   alongside `lifecycle` so a spec can wait on the semantic transition
 *   without filtering the broader stream.
 * @property resume - VM transitioned back to running (state ∈
 *   `'resuming' | 'running'` after a prior suspend).
 * @property expired - Engagement TTL hit, machine reaped (state ===
 *   `'terminating'`).
 * @property role - Driver/observer role change rendered in the
 *   observer banner.
 * @property chapter - Account-global chapter navigation pushed via
 *   the WS channel (a sibling tab moved on).
 */
export type LearnerEventsMap = {
  lifecycle: [{ state: MachineLifecycleState }]
  suspend: [{ reason: 'idle' | 'voluntary' }]
  resume: []
  expired: []
  role: [{ role: Role }]
  chapter: [ChapterRef]
}

/**
 * Browser-side event bus the SPA emits onto. Constructed once at
 * app boot in `main.tsx` and assigned to `window[LEARNER_EVENTS_WINDOW_KEY]`.
 *
 * `CoreClass`'s typed `.on/.once/.off/.emit` surface gives consumers
 * the framework-standard API. The bus is a pure notifier: no
 * persistence, no replay, no lifecycle (no transports, no Configure
 * phase) — listeners attached after the emission miss it.
 *
 * Test consumers (PersonaSession) tap in via Playwright's
 * `addInitScript` which buffers every emission into an array shared
 * back via `page.evaluate(...)`.
 */
export class LearnerEvents extends CoreClass<
  { id?: string },
  LearnerEventsMap
> {
  constructor(props: { id?: string } = {}) {
    super({ id: props.id ?? 'learner-events' })
  }
}

/**
 * Install the bus on `window` so test-side `addInitScript` taps can
 * find it. Idempotent — re-mounting in dev / StrictMode double-mount
 * reuses the existing instance.
 */
export function installLearnerEvents(): LearnerEvents {
  const w = window as unknown as Record<string, unknown>
  const existing = w[LEARNER_EVENTS_WINDOW_KEY]
  if (existing instanceof LearnerEvents) return existing
  const events = new LearnerEvents()
  w[LEARNER_EVENTS_WINDOW_KEY] = events
  return events
}

/**
 * Read the SPA's bus from `window`. Returns `null` outside the
 * browser (SSR / Node tests) and when `installLearnerEvents` hasn't
 * been called yet (renders before `main.tsx`'s composition).
 */
export function readLearnerEvents(): LearnerEvents | null {
  if (typeof window === 'undefined') return null
  const w = window as unknown as Record<string, unknown>
  const events = w[LEARNER_EVENTS_WINDOW_KEY]
  return events instanceof LearnerEvents ? events : null
}
